import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import useAnalytics from '../analytics/analytics';
import toastConfig from '../config/Toast';
import { formatCredits } from './format';
import type { CheckoutOutcome, CheckoutResponse, CreditPlan } from './types';

declare global {
  interface Window {
    rewardful: any;
    Rewardful: any;
  }
}

interface UseCheckoutOptions {
  /** Called after an in-place plan change (no redirect) so callers can refetch. */
  onChanged?: (outcome: Extract<CheckoutOutcome, { kind: 'changed' }>) => void;
}

/**
 * One checkout flow for the Billing tab and the Upgrade modal:
 * POST /billing/checkout either hands back a Stripe Checkout URL (new
 * subscription — we redirect) or confirms an in-place rung change on a live
 * subscription (we toast and let the caller refetch).
 */
const useCheckout = ({ onChanged }: UseCheckoutOptions = {}) => {
  const { post } = useApi();
  const { captureEvent } = useAnalytics();
  const [loading, setLoading] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [referralId, setReferralId] = useState<string | null>(null);

  useEffect(() => {
    if (window.rewardful) {
      window.rewardful('ready', () => {
        if (window.Rewardful && window.Rewardful.referral) {
          setReferralId(window.Rewardful.referral);
        }
      });
    }
  }, []);

  const checkout = useCallback(
    async (plan: Pick<CreditPlan, 'id' | 'name' | 'credits'>): Promise<CheckoutOutcome> => {
      setLoading(true);
      setPendingPlanId(plan.id);
      captureEvent('plan_selected', { plan_id: plan.id, plan_name: plan.name });
      try {
        const body: { plan_id: string; referral_id?: string } = { plan_id: plan.id };
        if (referralId) body.referral_id = referralId;
        const response = await post('/billing/checkout', body);
        const data = response?.data as CheckoutResponse | undefined;

        if (data && 'checkout_url' in data && data.checkout_url) {
          window.location.href = data.checkout_url;
          return { kind: 'redirect' };
        }
        if (data && 'changed' in data && data.changed) {
          const outcome = {
            kind: 'changed' as const,
            changeKind: data.change_kind,
            allotment: data.allotment,
          };
          toast.success(
            data.change_kind === 'downgrade'
              ? `Downgrade scheduled for your next renewal — ${formatCredits(plan.credits)} credits per month from then on.`
              : `Upgraded — ${formatCredits(data.allotment)} credits per month.`,
            toastConfig
          );
          setLoading(false);
          setPendingPlanId(null);
          onChanged?.(outcome);
          return outcome;
        }
        throw new Error('Unexpected checkout response');
      } catch (error: any) {
        const message: string =
          error?.detail || error?.message || 'Unable to start checkout. Please try again.';
        console.error('Checkout failed:', error);
        toast.error(message, toastConfig);
        setLoading(false);
        setPendingPlanId(null);
        return { kind: 'error', message };
      }
    },
    [post, referralId, captureEvent, onChanged]
  );

  return { checkout, loading, pendingPlanId };
};

export default useCheckout;
